/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import React, { Component } from 'react';
import ReactDOMServer from 'react-dom/server';
import { Chart } from 'react-google-charts';
import { getSDGShortTitle } from '../../../constants/sdg-data';
import NumberFormat from '../../../utils/number-format';
import { Loader } from '@g17eco/atoms/loader';
import { ScorecardGoal, ScorecardTarget } from '../../../types/scorecard';
import { SdgChangeFn } from '@features/impact-performance/SdgCharProps';
import { ReactGoogleChartEvent } from 'react-google-charts/dist/types';
import { InitiativePlain } from '../../../types/initiative';
import { getDisplayDecimalNumber } from '@utils/number';

export const CHART_MODES = {
  GOAL: 'goal',
  TARGET: 'target',
  NET: 'net'
}

interface Props {
  goals: ScorecardGoal[];
  initiative: Pick<InitiativePlain, 'materiality'>;
  handleOnClick?: SdgChangeFn;
  mode: string;
}

interface GoalData {
  contribution: string | number;
  scope: string;
  materiality: string;
  target: string | number;
}

export const getAbsoluteContribution = (target: ScorecardTarget) => {
  return (target.actual ?? 0) / 100
}

class SDGScoreChart extends Component<Props> {

  render() {
    const { goals, handleOnClick, mode } = this.props;

    const data = this.getChartData(goals);
    const chartEvents: ReactGoogleChartEvent[] = handleOnClick ? [
      {
        eventName: 'select',
        callback({ chartWrapper }) {
          const [firstSelectedItem] = chartWrapper.getChart().getSelection();
          if (firstSelectedItem) {
            if (firstSelectedItem.row >= 0) {
              const goal = firstSelectedItem.row + 1;
              const target = (firstSelectedItem.column + 2) / 3;
              if (mode === CHART_MODES.TARGET) {
                handleOnClick(`${goal}.${target}`);
              } else {
                handleOnClick(goal);
              }
            }
          }
        }
      }
    ] : [];

    const width = 900;
    const height = 300;
    return (
      <Chart
        chartType='ColumnChart'
        data={data}
        chartEvents={chartEvents}
        loader={< Loader />}
        options={{
          bar: { groupWidth: '93%' },
          isStacked: mode !== CHART_MODES.TARGET,
          chartArea: {
            backgroundColor: {
              stroke: '#f5f5f0',
              strokeWidth: 4,
              fill: '#f5f5f0',
            },
            left: 50,
            top: 10,
            bottom: 5,
            width: width,
            height: height - 10
          },
          tooltip: { isHtml: true, trigger: 'visible' },
          legend: { position: 'none' },
          vAxis: {
            baselineColor: '#FFF',
            textStyle: {
              fontSize: 10,
              bold: true,
            },
            viewWindowMode: 'explicit',
            viewWindow: {
              max: 1,
              min: mode === CHART_MODES.GOAL ? 0 : -1
            },
            format: 'percent'
          },
          hAxis: {
            gridlines: {
              color: '#FFF',
              count: 17
            }
          }
        }
        }
        width={width}
        height={height}
      />
    );
  }

  getSDGColor(sdg: number) {
    const colours = [
      '#f1002f',
      '#d99e3c',
      '#219b52',
      '#c81035',
      '#f83631',
      '#00aed9',
      '#ffb437',
      '#921036',
      '#f56a2f',
      '#e60080',
      '#ff993a',
      '#d48a37',
      '#497744',
      '#007db8',
      '#3bb056',
      '#005586',
      '#0c3866'
    ];
    return colours[sdg - 1];
  }

  getContributionTooltip = (sdgCode: number, contributionData: GoalData) => {
    const el = <div className='p-2' style={{ minWidth: '240px' }}>
      <strong>SDG {sdgCode} {getSDGShortTitle(sdgCode)}</strong>
      <div className='row g-0'>
        <div className='col-6 text-right'>Contribution:</div>
        <div className='col-6 text-left pl-2'>{contributionData.contribution}%</div>
      </div>
      <div className='row g-0'>
        <div className='col-6 text-right'>Scope:</div>
        <div className='col-6 text-left pl-2'>{contributionData.scope}</div>
      </div>
      <div className='row g-0'>
        <div className='col-6 text-right'>Materiality:</div>
        <div className='col-6 text-left pl-2'>{contributionData.materiality}</div>
      </div>
    </div>;

    return ReactDOMServer.renderToString(el);
  }

  getNetContributionTooltip = (sdgCode: number, netPositiveAvg: number, netNegativeAvg: number) => {
    const el = <div className='p-2' style={{ minWidth: '400px' }}>
      <strong>SDG {sdgCode} {getSDGShortTitle(sdgCode)}</strong>
      <div className='row g-0'>
        <div className='col-8 text-right'>Positive Contribution:</div>
        <div className='col-4 text-left pl-2'><NumberFormat value={netPositiveAvg} decimalPlaces={0} suffix='%' /></div>
      </div>
      <div className='row g-0'>
        <div className='col-8 text-right'>Negative Contribution:</div>
        <div className='col-4 text-left pl-2'><NumberFormat value={netNegativeAvg} decimalPlaces={0} suffix='%' /></div>
      </div>
    </div>;

    return ReactDOMServer.renderToString(el);
  }

  getTargetContributionTooltip = (sdgCode: string, contribution: number) => {
    const el = <div className='p-2' style={{ minWidth: '400px' }}>
      <strong>SDG {sdgCode} {getSDGShortTitle(sdgCode)}</strong>
      <div className='row g-0'>
        <div className='col-8 text-right'>Contribution:</div>
        <div className='col-4 text-left pl-2'><NumberFormat value={contribution} decimalPlaces={0} suffix='%' /></div>
      </div>
    </div>;

    return ReactDOMServer.renderToString(el);
  }

  getTargetTooltip = (sdgCode: number, contributionData: GoalData) => {
    const el = <div className='p-2' style={{ minWidth: '240px' }}>
      <strong>SDG {sdgCode} {getSDGShortTitle(sdgCode)}</strong>
      <div className='row g-0'>
        <div className='col-6 text-right'>Target Contribution:</div>
        <div className='col-6 text-left pl-2'>{contributionData.target}%</div>
      </div>
    </div>
    return ReactDOMServer.renderToString(el);
  }

  getGoalData = (goalScorecard: ScorecardGoal): GoalData => {
    const contribution = typeof goalScorecard.actual === 'number' ? getDisplayDecimalNumber(goalScorecard.actual) : 0;
    const target = typeof goalScorecard.target === 'number' ? getDisplayDecimalNumber(goalScorecard.target) : 0;

    const totalScope = goalScorecard.targets.length;
    const inScope = goalScorecard.targets.reduce((a, t) => a + (t.inScope ? 1 : 0), 0);

    const materialityPc = this.props.initiative?.materiality?.[goalScorecard.sdgCode] ?? 0;
    const materiality = materialityPc > 66 ? 'High' : materialityPc > 33 ? 'Medium' : materialityPc > 0 ? 'Low' : 'None';

    return {
      contribution,
      scope: `${inScope} of ${totalScope} Targets`,
      materiality,
      target,
    };
  }

  getChartData(goals: ScorecardGoal[]) {
    const { mode = CHART_MODES.GOAL } = this.props;
    switch (mode) {
      case CHART_MODES.TARGET:
        return this.getTargetContributionData(goals)
      case CHART_MODES.NET:
        return this.getNetContributionData(goals)
      case CHART_MODES.GOAL:
      default:
        return this.getContributionData(goals);
    }
  }

  getNetContributionData(goals: ScorecardGoal[]) {
    const columns: unknown[][] = [
      [
        'SDG',
        'Adding',
        { role: 'style' },
        { type: 'string', role: 'tooltip', p: { html: true } },
        'Removing',
        { role: 'style' },
        { type: 'string', role: 'tooltip', p: { html: true } },
      ],
    ];

    for (let i = 1; i <= 17; i++) {
      const colour = this.getSDGColor(i);
      columns.push([i, 0, 'color: ' + colour + '; opacity:0.8', '', 0, 'color: ' + colour + '; opacity:0.2', '']);
    }
    goals.forEach((goal, i) => {
      if (!goal.actual) {
        return;
      }

      const targets = goal.targets.filter(t => t.inScope);
      const netContributions = targets.map(t => this.getNetContribution(t.actual));

      const netPositiveAvg = netContributions.filter(nc => nc >= 0).reduce((acc, nc) => acc + nc, 0) / targets.length;
      const netNegativeAvg = netContributions.filter(nc => nc < 0).reduce((acc, nc) => acc + nc, 0) / targets.length;

      columns[i + 1][1] = netPositiveAvg / 100;
      columns[i + 1][3] = this.getNetContributionTooltip(i + 1, netPositiveAvg, netNegativeAvg)
      columns[i + 1][4] = netNegativeAvg / 100;
      columns[i + 1][6] = this.getNetContributionTooltip(i + 1, netPositiveAvg, netNegativeAvg)
    });

    return columns;
  }


  getNetContribution = (actual?: number) => {
    return 2 * ((actual ?? 0) - 50);
  }

  getTargetContributionData(goals: ScorecardGoal[]) {
    const maxTargets = goals.reduce((acc, g) => {
      const count = g.targets.filter(t => t.inScope).length;
      return count > acc ? count : acc;
    }, 0);

    const rows = [];
    const headerColumns: unknown[] = [
      'SDG',
    ];
    for (let c = 0; c < maxTargets; c++) {
      headerColumns.push('Contribution');
      headerColumns.push({ role: 'style' });
      headerColumns.push({ type: 'string', role: 'tooltip', p: { html: true } });
    }
    rows.push(headerColumns);

    goals.forEach((goal, i) => {
      const colour = this.getSDGColor(i + 1);
      const columns: unknown[] = [
        `Goal ${goal.sdgCode}`,
      ];

      for (let c = 0; c < maxTargets; c++) {
        const target = goal.targets.find(t => t.sdgCode === `${goal.sdgCode}.${c + 1}`);
        const style = `color: ${colour}; stroke-width: 0.25; stroke-color: #F5F5F0`;
        if (goal.actual && target && target.inScope) {
          const contribution = getAbsoluteContribution(target);
          columns.push(contribution);
          columns.push(style);
          columns.push(this.getTargetContributionTooltip(target.sdgCode, contribution));
        } else {
          columns.push(0);
          columns.push(style);
          columns.push('');
        }
      }
      rows.push(columns);
    });

    return rows;
  }

  getContributionData(goals: ScorecardGoal[]) {
    const columns: unknown[][] = [
      [
        'SDG',
        'Current',
        { role: 'style' },
        { type: 'string', role: 'tooltip', p: { html: true } },
        'Target',
        { role: 'style' },
        { type: 'string', role: 'tooltip', p: { html: true } },
      ],
    ];

    for (let i = 1; i <= 17; i++) {
      const colour = this.getSDGColor(i);
      columns.push([i, 0, 'color: ' + colour + '; opacity:0.8', '', 0, 'color: ' + colour + '; opacity:0.2', '']);
    }
    goals.forEach((goal, i) => {
      const contributionData = this.getGoalData(goal);
      const targetValue = goal.target || 0;
      const actual = goal.actual || 0;

      if (actual) {
        columns[i + 1][1] = actual / 100;
        columns[i + 1][3] = this.getContributionTooltip(i + 1, contributionData)
      }
      columns[i + 1][4] = (targetValue - actual) / 100;
      columns[i + 1][6] = this.getTargetTooltip(i + 1, contributionData)
    });

    return columns;
  }
}

export default SDGScoreChart;
